import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { notificationAPI, settingsAPI } from '../services/api';
import { hasPermission, getDashboardPath, getRoleDisplay, isSystemRole } from '../utils/roleUtils';
import { getNavItems } from '../utils/navItems';
import Icon from './Icon';

function Navbar({ user, onLogout }) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('sidebar_collapsed') || 'false');
    } catch {
      return false;
    }
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [branding, setBranding] = useState({ platformName: 'Recruiters', logoUrl: '/assets/logo.png' });

  useEffect(() => {
    settingsAPI.getPublic()
      .then((res) => {
        const general = (res.data && res.data.general) || {};
        setBranding({
          platformName: general.platformName || 'OUK Recruiter Platform',
          logoUrl: general.logoUrl || '/assets/logo.png'
        });
      })
      .catch(() => {});
  }, []);

  const isExact = (path) => location.pathname === path;
  const isPrefix = (path) => location.pathname.startsWith(path);

  const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username)}&background=037b90&color=fff&size=32`;

  const isSystemUser = isSystemRole(user.role);
  const canViewNotifications = isSystemUser ?
    (user.role === 'recruiter' || user.role === 'admin' || user.role === 'comms_marketing') :
    hasPermission(user.permissions, 'dashboard.view');

  const notifPath = user.role === 'admin' ? '/admin/notifications' :
    user.role === 'comms_marketing' ? '/comms/notifications' : '/recruiter/notifications';

  useEffect(() => {
    // Load unread notifications count for users with notification permissions
    if (canViewNotifications) {
      loadUnreadCount();

      // Set up interval to check for new notifications every 30 seconds
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user.permissions, user.role]);

  const loadUnreadCount = async () => {
    try {
      const isAdmin = isSystemUser ?
        user.role === 'admin' :
        hasPermission(user.permissions, 'system.audit');
      const params = isAdmin ? { admin: true } : {};
      const response = await notificationAPI.getAll(params);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      // Silently fail for rate limit errors - don't show error for notification count
      if (!error.message?.includes('Too many requests')) {
        console.error('Failed to load notification count:', error);
      }
      // Set count to 0 on error to avoid UI issues
      setUnreadCount(0);
    }
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen((open) => !open);
  const closeMenu = () => setIsMobileMenuOpen(false);

  const toggleCollapse = () => {
    setIsCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        localStorage.setItem('sidebar_collapsed', JSON.stringify(next));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  };

  const navItem = (to, icon, label, active) => (
    <Link
      key={to}
      to={to}
      className={`navbar-link ${active ? 'active' : ''}`}
      onClick={closeMenu}
      title={label}
    >
      <span className="nav-link-icon">
        <Icon name={icon} />
      </span>
      <span className="nav-link-label">{label}</span>
    </Link>
  );

  const navItems = getNavItems(user).map((item) => {
    const active = item.exact ? isExact(item.to) : isPrefix(item.to);
    return navItem(item.to, item.icon, item.label, active);
  });

  const notificationBell = (
    <span className="nav-link-icon notification-icon-container">
      <span className="notification-icon">
        <Icon name="bell" />
      </span>
      {unreadCount > 0 && (
        <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
      )}
    </span>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="navbar-mobile-topbar">
        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <Link to={getDashboardPath(user.permissions, user.role)} className="navbar-brand" onClick={closeMenu} title="Go to dashboard">
          <img src={branding.logoUrl} alt="OUK Logo" className="navbar-logo" />
          <span className="topbar-brand-text">{branding.platformName}</span>
        </Link>

        {canViewNotifications && (
          <Link
            to={notifPath}
            className="notification-icon-link"
            title={`${unreadCount} unread notifications`}
            onClick={closeMenu}
          >
            {notificationBell}
          </Link>
        )}

        <div className="user-avatar topbar-avatar">
          <img src={avatarUrl} alt={user.username} className="user-avatar-img" />
        </div>
      </div>

      {/* Desktop sidebar / mobile drawer */}
      <aside className={`sidebar navbar ${isCollapsed ? 'sidebar-collapsed' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="navbar-container">
          <div className="navbar-header">
            <Link to={getDashboardPath(user.permissions, user.role)} className="navbar-brand" onClick={closeMenu} title="Go to dashboard">
              <img src={branding.logoUrl} alt="OUK Logo" className="navbar-logo" />
              <span className="sidebar-brand-text">{branding.platformName}</span>
            </Link>

            <button
              className="sidebar-toggle"
              onClick={toggleCollapse}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon name="menu" />
            </button>

            <button className="sidebar-close" onClick={closeMenu} aria-label="Close menu">
              <Icon name="close" />
            </button>
          </div>

          <nav className="navbar-nav">
            {navItems}
          </nav>

          <div className="navbar-footer">
            {canViewNotifications && (
              <Link
                to={notifPath}
                className="navbar-link nav-notif-link"
                onClick={closeMenu}
                title={`${unreadCount} unread notifications`}
              >
                {notificationBell}
                <span className="nav-link-label">Notifications</span>
              </Link>
            )}

            <div className="navbar-user">
              <div className="user-avatar">
                <img src={avatarUrl} alt={user.username} className="user-avatar-img" />
              </div>
              <div className="user-info">
                <div className="user-name">{user.username}</div>
                <div className="user-role">{getRoleDisplay(user.role).displayName}</div>
              </div>
              <button onClick={() => { onLogout(); closeMenu(); }} className="logout-btn" title="Logout">
                <span className="nav-link-icon">
                  <Icon name="logout" />
                </span>
                <span className="nav-link-label logout-label">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div className={`sidebar-backdrop ${isMobileMenuOpen ? 'show' : ''}`} onClick={closeMenu} />
    </>
  );
}

export default Navbar;